import React from 'react';
import { useQuery } from '@tanstack/react-query';

import Modal from '../../components/ui/Modal';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import ErrorBanner from '../../components/ui/ErrorBanner';
import CurrencyDisplay from '../../components/shared/CurrencyDisplay';
import DateDisplay from '../../components/shared/DateDisplay';

import { getBudget } from '../../api/budgets';
import { formatPercent } from '../../utils/currency';

function severityFor(pct) {
  const n = Number(pct) || 0;
  if (n >= 90) return { bar: 'bg-red-500', tone: 'danger', label: 'Critical' };
  if (n >= 70) return { bar: 'bg-amber-500', tone: 'warning', label: 'High' };
  return { bar: 'bg-green-500', tone: 'success', label: 'Healthy' };
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className="mt-1 text-base font-semibold text-ink">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
        {label}
      </div>
      <div className="mt-1 text-sm text-ink">{children}</div>
    </div>
  );
}

/**
 * Read-only detail view for a single budget. Re-fetches the budget by id
 * so usage / utilization figures are always fresh, falling back to the
 * row passed in from the table while loading.
 */
export default function BudgetDetailModal({ open, budgetId, fallback, onClose }) {
  const query = useQuery({
    queryKey: ['budgets', budgetId],
    queryFn: () => getBudget(budgetId),
    enabled: !!open && !!budgetId,
  });

  const budget = query.data || fallback || null;
  const pct = Number(budget?.utilizationPercent ?? 0);
  const sev = severityFor(pct);
  const clamped = Math.max(0, Math.min(100, pct));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={budget ? `${budget.department} · ${budget.category}` : 'Budget detail'}
      description={
        budget ? `FY ${budget.year} · Q${budget.quarter}` : 'Loading budget…'
      }
      size="lg"
      footer={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {query.isLoading && !fallback ? (
        <LoadingSpinner label="Loading budget..." />
      ) : query.error && !fallback ? (
        <ErrorBanner error={query.error} title="Could not load budget" />
      ) : budget ? (
        <div className="flex flex-col gap-6">
          <div className="rounded-lg border border-slate-200 bg-white px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm font-medium text-ink">Utilization</div>
              <div className="flex items-center gap-2">
                <Badge tone={sev.tone}>{sev.label}</Badge>
                <span className="text-sm font-semibold tabular-nums text-ink">
                  {formatPercent(pct, 1)}
                </span>
              </div>
            </div>
            <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-slate-200">
              <div
                className={`h-full ${sev.bar} transition-all`}
                style={{ width: `${clamped}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-ink-muted">
              <span>
                <CurrencyDisplay value={budget.usedAmount} /> used
              </span>
              <span>
                <CurrencyDisplay value={budget.allocatedAmount} /> allocated
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Stat label="Allocated" value={<CurrencyDisplay value={budget.allocatedAmount} />} />
            <Stat label="Used" value={<CurrencyDisplay value={budget.usedAmount} />} />
            <Stat label="Remaining" value={<CurrencyDisplay value={budget.remainingAmount} />} />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Department">{budget.department}</Field>
            <Field label="Category">{budget.category}</Field>
            <Field label="Project ID">
              {budget.projectId != null ? `#${budget.projectId}` : (
                <span className="text-ink-faint">—</span>
              )}
            </Field>
            <Field label="Period">FY {budget.year} · Q{budget.quarter}</Field>
            <Field label="Created by">
              {budget.createdBy || <span className="text-ink-faint">—</span>}
            </Field>
            <Field label="Created at">
              <DateDisplay value={budget.createdAt} withTime />
            </Field>
            <Field label="Budget ID">
              <span className="font-mono text-xs text-ink-muted">#{budget.id}</span>
            </Field>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
